import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, cn, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, formatSquareMetres, refusalMessage } from "../../api/manufacturing";
import { ProductionOrderClient, type PanelPlanDTO } from "../../api/production";

export const panelPlansKey = (orderId: number) => ["production", "production-order", orderId, "panel-plans"];

/**
 * The production BOM's panel plan (docs/cutting-jobs §4): for each virtual
 * part, the real panels it is cut from and how many of each, as Cut Rite plans
 * it (10 × 5'×10' and 15 × 5'×8'). Tick a panel, give its count; the coverage
 * says what the chosen sheets give at the expected yield against what the bill
 * needs. Confirming the production BOM needs panels chosen for each; under
 * 100 % is allowed (Cut Rite may do better than the expected yield) but shown.
 */
export function PanelPlanEditor({ orderId, editable, onChanged }: { orderId: number; editable: boolean; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new ProductionOrderClient(http), [http]);
  const plans = useQuery({ queryKey: panelPlansKey(orderId), queryFn: () => client.panelPlans(orderId) });
  const shown = (plans.data ?? []).filter((plan) => plan.needed > 0 || plan.lines.some((line) => line.quantity > 0));
  if (shown.length === 0) return null;
  return (
    <section className="border-b border-line px-4 py-3 lg:px-6">
      <div className="mb-2 flex flex-wrap items-baseline gap-2">
        <h3 className="text-[13px] font-semibold">{t("panel-plan.title", "Panels")}</h3>
        <span className="text-[12px] text-ink-muted">
          {editable
            ? t("panel-plan.hint", "Tick the panels each material is cut from and how many of each, as Cut Rite plans it.")
            : t("panel-plan.hint.locked", "As planned when the production BOM was confirmed.")}
        </span>
      </div>
      <div className="grid gap-3 xl:grid-cols-2">
        {shown.map((plan) => (
          <PlanCard key={plan.virtual_part_id} orderId={orderId} plan={plan} editable={editable} onChanged={onChanged} />
        ))}
      </div>
    </section>
  );
}

function needLabel(plan: PanelPlanDTO, value: number): string {
  return plan.kind === "STRIP" ? `${formatNumber(Math.round(value / 10) / 100)} m` : formatSquareMetres(value);
}

function PlanCard({ orderId, plan, editable, onChanged }: { orderId: number; plan: PanelPlanDTO; editable: boolean; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new ProductionOrderClient(http), [http]);
  const saved = useMemo(() => Object.fromEntries(plan.lines.map((line) => [line.production_order_material_id, Number(line.quantity)])), [plan]);
  const [draft, setDraft] = useState<Record<number, number>>(saved);
  const dirty = plan.lines.some((line) => (draft[line.production_order_material_id] ?? 0) !== saved[line.production_order_material_id]);
  const planned = plan.lines.reduce((sum, line) => sum + (draft[line.production_order_material_id] ?? 0) * Number(line.capacity ?? 0), 0);
  const coverage = plan.needed > 0 ? planned / plan.needed : null;
  const save = useMutation({
    mutationFn: () =>
      client.setPanelPlan(orderId, plan.virtual_part_id, plan.lines.map((line) => ({
        production_order_material_id: line.production_order_material_id,
        quantity: draft[line.production_order_material_id] ?? 0,
      }))),
    onSuccess: async (next) => {
      queryClient.setQueryData(panelPlansKey(orderId), next);
      await onChanged();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  // Ticking a panel suggests what is still to cover, in its sheets.
  const suggest = (capacity: number | null, whole: boolean) => {
    if (!capacity || capacity <= 0) return 1;
    const left = Math.max(0, plan.needed - planned);
    const sheets = left / capacity;
    return whole ? Math.max(1, Math.ceil(sheets)) : Math.max(0.5, Math.round(sheets * 100) / 100);
  };
  const lines = editable ? plan.lines : plan.lines.filter((line) => Number(line.quantity) > 0);
  return (
    <div className="rounded-md border border-line">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
        <span className="font-mono text-[13px] font-semibold">{plan.virtual_number}</span>
        <span className="text-[12px] text-ink-muted">{t("panel-plan.needs", "needs {{n}}", { n: needLabel(plan, plan.needed) })}</span>
        {coverage != null && (
          <Badge size="sm" variant={coverage >= 1 ? "success" : coverage > 0 ? "warning" : "error"}>
            {t("panel-plan.covers", "covers {{p}} %", { p: Math.round(coverage * 100) })}
          </Badge>
        )}
        {plan.cut_job_id != null && (
          <Link to="/production/cut-jobs/$jobId" params={{ jobId: String(plan.cut_job_id) }} className="ml-auto text-[12px] text-accent hover:underline">
            {t("panel-plan.in-job", "Cut job {{n}}", { n: plan.cut_job_number })}
          </Link>
        )}
      </div>
      <ul className="divide-y divide-line">
        {lines.map((line) => {
          const id = line.production_order_material_id;
          const value = draft[id] ?? 0;
          const on = value > 0;
          return (
            <li key={id} className="flex items-center gap-3 px-3 py-1.5 text-[13px]">
              <input
                type="checkbox"
                className="size-4 accent-[var(--color-accent)]"
                checked={on}
                disabled={!editable || save.isPending || line.capacity == null}
                title={line.capacity == null ? t("panel-plan.no-size", "No sheet size on file: it cannot be planned.") : undefined}
                onChange={(event) => setDraft({ ...draft, [id]: event.target.checked ? suggest(line.capacity, line.is_consumed_whole) : 0 })}
              />
              <span className="font-mono">{line.number}</span>
              <span className="min-w-0 flex-1 truncate text-ink-muted">{line.name}</span>
              {line.capacity != null && (
                <span className="hidden text-[12px] text-ink-muted sm:inline">
                  {t("panel-plan.each", "{{a}} each", { a: needLabel(plan, Number(line.capacity)) })}
                </span>
              )}
              {editable ? (
                <input
                  type="number"
                  min={0}
                  step={line.is_consumed_whole ? 1 : 0.01}
                  value={on ? value : ""}
                  disabled={!on || save.isPending}
                  onChange={(event) => {
                    const next = Number(event.target.value);
                    setDraft({ ...draft, [id]: Number.isFinite(next) && next >= 0 ? (line.is_consumed_whole ? Math.round(next) : next) : 0 });
                  }}
                  className={cn("h-8 w-20 rounded-md border border-line bg-surface px-2 text-right tabular-nums", !on && "opacity-40")}
                  aria-label={t("panel-plan.quantity", "How many")}
                />
              ) : (
                <span className="w-20 text-right tabular-nums">{formatNumber(value)}</span>
              )}
              <span className="w-8 text-[12px] text-ink-muted">{line.unit_code}</span>
            </li>
          );
        })}
      </ul>
      {editable && dirty && (
        <div className="flex items-center justify-end gap-2 border-t border-line px-3 py-2">
          <Button size="sm" variant="ghost" disabled={save.isPending} onClick={() => setDraft(saved)}>
            {t("panel-plan.undo", "Undo")}
          </Button>
          <Button size="sm" variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            {t("panel-plan.save", "Save the panels")}
          </Button>
        </div>
      )}
    </div>
  );
}
